import type { FC } from "react";

import { Button } from "src/components";
import { useTranslation } from "src/translations";
import type { QuestionnaireAnswers } from "./hooks/useQuestionnaireAnswers";
import { isConditionalVisible, type QuestionGroup } from "./hooks/useQuestionnaireGroups";
import { QuestionInput } from "./QuestionInput";

type QuestionnaireStepProps = {
    group: QuestionGroup;
    groupIndex: number;
    totalGroups: number;
    answers: QuestionnaireAnswers;
    onRootChange: (value: string | string[]) => void;
    onConditionalChange: (questionId: string, value: string | string[]) => void;
    onNext: () => void;
    onPrev: () => void;
    isFirst: boolean;
    isLast: boolean;
    isSubmitting: boolean;
    showErrors: boolean;
};

export const QuestionnaireStep: FC<QuestionnaireStepProps> = ({
    group,
    groupIndex,
    totalGroups,
    answers,
    onRootChange,
    onConditionalChange,
    onNext,
    onPrev,
    isFirst,
    isLast,
    isSubmitting,
    showErrors,
}) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-6">
            <p className="text-sm text-secondary">
                {t("verification.INVESTOR_QUESTIONNAIRE.step", { current: groupIndex + 1, total: totalGroups })}
            </p>

            <QuestionInput question={group.root} value={answers[group.root.questionId]} onChange={onRootChange} showError={showErrors} />

            {group.conditionals.map((q) =>
                isConditionalVisible(group, answers, q) ? (
                    <QuestionInput
                        key={q.questionId}
                        question={q}
                        value={answers[q.questionId]}
                        onChange={(value) => onConditionalChange(q.questionId, value)}
                        showError={showErrors}
                    />
                ) : null,
            )}

            <div className="flex justify-between gap-4 pt-2">
                <Button kind="outline" type="button" onClick={onPrev} disabled={isFirst}>
                    {t("verification.INVESTOR_QUESTIONNAIRE.previous")}
                </Button>
                <Button type="button" onClick={onNext} isLoading={isSubmitting}>
                    {isLast ? t("verification.INVESTOR_QUESTIONNAIRE.submit") : t("verification.INVESTOR_QUESTIONNAIRE.next")}
                </Button>
            </div>
        </div>
    );
};
